
import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaPlay } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const RaiseYourVoiceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    underReview: 0,
    approved: 0,
    rejected: 0,
    running: 0,
    completed: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/user/get/ryv/ad/stats`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched all Voices Data:", response);
        // Expecting response.data.stats
        if (response.data && response.data.stats) {
          setStats({
            underReview: response.data.stats.underReview || 0,
            approved: response.data.stats.approved || 0,
            rejected: response.data.stats.rejected || 0,
            running: response.data.stats.running || 0,
            completed: response.data.stats.completed || 0,
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching voice stats:", error);
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Handle card click to navigate to specific pages
  const handleCardClick = (type) => {
    switch (type) {
      case "approved":
        navigate("/raise/approvedvoice");
        break;
      case "underreview":
        navigate("/raise/underreviewvoice");
        break;
      case "running":
        navigate("/raise/runningvoice");
        break;
      case "rejected":
        navigate("/raise/rejectedvoice");
        break;
      case "completed":
        navigate("/raise/history");
        break;
      default:
        break;
    }
  };

  return (
    <div className="p-6">
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        .animate-heartbeat {
          animation: heartbeat 2.5s ease-in-out infinite;
        }
      `}</style>


      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        Voice Status
      </h1>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        {/* Under Review Voices */}
        <div
          className="bg-orange-500 p-6 rounded-xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => handleCardClick("underreview")}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">Under Review Voices</p>
              <p className="text-2xl font-bold mt-1">
                {loading ? "0" : stats.underReview}
              </p>
            </div>
            <FaHourglassHalf className="w-10 h-10 opacity-80" />
          </div>
        </div>
        {/* Approved Voices */}
        <div
          className="bg-yellow-500 p-6 rounded-xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => handleCardClick("approved")}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">Approved Voices</p>
              <p className="text-2xl font-bold mt-1">
                {loading ? "0" : stats.approved}
              </p>
            </div>
            <FaCheckCircle className="w-10 h-10 opacity-80" />
          </div>
        </div>

        {/* Running voices */}
        <div
          className="bg-blue-500 p-6 rounded-xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => handleCardClick("running")}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">Running Voices</p>
              <p className="text-2xl font-bold mt-1">
                {loading ? "0" : stats.running}
              </p>
            </div>
            <FaCheckCircle className="w-10 h-10 opacity-80" />
          </div>
        </div>

        {/* Rejected Voices */}
        <div
          className="bg-red-500 p-6 rounded-xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => handleCardClick("rejected")}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">Rejected Voices</p>
              <p className="text-2xl font-bold mt-1">
                {loading ? "0" : stats.rejected}
              </p>
            </div>
            <FaTimesCircle className="w-10 h-10 opacity-80" />
          </div>
        </div>
        {/* Completed Voices */}
        <div
          className="bg-green-500 p-6 rounded-xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => handleCardClick("completed")}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium opacity-80">Completed Voices</p>
              <p className="text-2xl font-bold mt-1">
                {loading ? "0" : stats.completed}
              </p>
            </div>
            <FaCheckCircle className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>
      {/* Instructions */}
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          View Your Voices
        </h3>
        <p className="text-gray-500 mb-4">
          Click on any of the cards above to view detailed information about your voice submissions.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
          <div className="p-4 bg-orange-50 rounded-lg text-orange-800">
            <p className="font-medium">Under Review</p>
            <p className="text-sm mt-1">Voices pending admin review</p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg text-yellow-800">
            <p className="font-medium">Approved</p>
            <p className="text-sm mt-1">Admin approved voices</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg text-blue-800">
            <p className="font-medium">Running</p>
            <p className="text-sm mt-1">Reporters working on voices</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg text-red-800">
            <p className="font-medium">Rejected</p>
            <p className="text-sm mt-1">Admin rejected voices</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg text-green-800">
            <p className="font-medium">Completed</p>
            <p className="text-sm mt-1">Finished work by reporters</p>
          </div>
        </div>
      </div>

      {/* Red Floating Video Guide Banner with Direct Embed - Bottom */}
      <div className="mt-8 bg-gradient-to-br from-red-600 via-rose-600 to-red-700 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-300 hover:shadow-red-500/20">
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
            <span className="bg-white/20 text-white text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest animate-pulse flex items-center gap-1.5">
              <FaPlay className="text-[8px] ml-0.5" /> Direct Guide
            </span>
            <span className="text-red-100 text-xs font-bold tracking-wide">Watch &amp; Learn</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-[1000] tracking-tight mb-2 uppercase">How to use Raise Your Voice</h2>
          <p className="text-xs sm:text-sm text-red-100 leading-relaxed max-w-md opacity-90">
            Watch this quick tutorial to learn how to raise your voice, check status updates, and view completed cases directly from your dashboard.
          </p>
        </div>

        {/* Embedded YouTube Video Player directly */}
        <div className="w-full max-w-[200px] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 shrink-0 transition-transform duration-700 hover:scale-105 animate-heartbeat">
          <iframe
            src="https://www.youtube.com/embed/iBWrXZcR5Hs"
            title="How to use Raise Your Voice"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </div>
      </div>
    </div>
  );
};

export default RaiseYourVoiceDashboard;
